import React, { useEffect, useState } from 'react';
import { FormField } from './FormField';
import { PlusIcon, TrashIcon, CalendarIcon, MailIcon, SmartphoneIcon, CalendarDaysIcon, UsersIcon, BuildingIcon, PercentIcon, DollarSignIcon, AlertCircleIcon, InfoIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
import { SegmentedControl } from './SegmentedControl';
export interface Recipient {
  id: number;
  email: string;
  phone: string;
}
export interface Supplier {
  id: number;
  name: string;
  email: string;
  amount: string;
  percentage?: string;
}
export interface Installment {
  id: number;
  amount: string;
  dueDate: string;
  sendLinkDate: string;
  description: string;
  recipient?: Recipient;
}
interface InstallmentPaymentProps {
  onExpiryDateChange?: (date: string) => void;
}
export function InstallmentPayment({
  onExpiryDateChange
}: InstallmentPaymentProps) {
  const [installments, setInstallments] = useState<Installment[]>([{
    id: 1,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 1,
      email: '',
      phone: ''
    }
  }, {
    id: 2,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 2,
      email: '',
      phone: ''
    }
  }]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([{
    id: 1,
    name: '',
    email: '',
    amount: '',
    percentage: '0'
  }]);
  const [splitType, setSplitType] = useState<'dates' | 'recipients' | 'commission'>('dates');
  const [beneficiaryType, setBeneficiaryType] = useState<'percentage' | 'fixed'>('percentage');
  const [feeType, setFeeType] = useState<'supplier' | 'merchant' | 'split'>('supplier');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [totalPaymentAmount, setTotalPaymentAmount] = useState('');
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [supplierCommissionTotal, setSupplierCommissionTotal] = useState(0);
  const [showDatePickers, setShowDatePickers] = useState<{
    [key: number]: {
      due: boolean;
      send: boolean;
    };
  }>({});
  // This would come from settings in a real app
  const defaultExpiryDays = 30;
  // Validate commission amounts don't exceed total payment
  useEffect(() => {
    if (splitType === 'commission' && totalPaymentAmount) {
      const totalAmount = parseFloat(totalPaymentAmount) || 0;
      // Calculate total commission amount
      const commissionTotal = suppliers.reduce((total, supplier) => {
        if (beneficiaryType === 'percentage') {
          // For percentage, we'll check in another validation
          return total;
        } else {
          // For fixed amount
          return total + (parseFloat(supplier.amount) || 0);
        }
      }, 0);
      setSupplierCommissionTotal(commissionTotal);
      if (beneficiaryType === 'fixed' && commissionTotal > totalAmount) {
        setCommissionError('Total commission amount cannot exceed the total payment amount');
      } else if (beneficiaryType === 'percentage') {
        const percentageTotal = suppliers.reduce((total, supplier) => {
          return total + (parseFloat(supplier.percentage || '0') || 0);
        }, 0);
        if (percentageTotal > 100) {
          setCommissionError('Total commission percentage cannot exceed 100%');
        } else {
          setCommissionError(null);
        }
      } else {
        setCommissionError(null);
      }
    }
  }, [splitType, totalPaymentAmount, suppliers, beneficiaryType]);
  // Calculate send link date when due date changes
  const calculateSendLinkDate = (dueDateStr: string): string => {
    if (!dueDateStr) return '';
    try {
      // Parse the due date
      const [month, day, year] = dueDateStr.split('/').map(part => parseInt(part));
      const dueDate = new Date(year, month - 1, day);
      // Subtract the default expiry days
      const sendDate = new Date(dueDate);
      sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
      // Format the date as MM/DD/YYYY
      const sendMonth = (sendDate.getMonth() + 1).toString().padStart(2, '0');
      const sendDay = sendDate.getDate().toString().padStart(2, '0');
      const sendYear = sendDate.getFullYear();
      return `${sendMonth}/${sendDay}/${sendYear}`;
    } catch (error) {
      // If there's any error parsing, return empty string
      return '';
    }
  };
  const addInstallment = () => {
    const newId = Math.max(...installments.map(i => i.id), 0) + 1;
    setInstallments([...installments, {
      id: newId,
      amount: '',
      dueDate: '',
      sendLinkDate: '',
      description: '',
      recipient: {
        id: newId,
        email: '',
        phone: ''
      }
    }]);
  };
  const addSupplier = () => {
    const newId = Math.max(...suppliers.map(s => s.id), 0) + 1;
    setSuppliers([...suppliers, {
      id: newId,
      name: '',
      email: '',
      amount: '',
      percentage: '0'
    }]);
  };
  const removeInstallment = (id: number) => {
    if (installments.length > 1) {
      setInstallments(installments.filter(i => i.id !== id));
    }
  };
  const removeSupplier = (id: number) => {
    if (suppliers.length > 1) {
      setSuppliers(suppliers.filter(s => s.id !== id));
    }
  };
  const updateInstallment = (id: number, field: string, value: string) => {
    setInstallments(installments.map(installment => {
      if (installment.id === id) {
        const updatedInstallment = {
          ...installment,
          [field]: value
        };
        // If we're updating the due date and it's a valid date, update the send link date
        if (field === 'dueDate' && value) {
          updatedInstallment.sendLinkDate = calculateSendLinkDate(value);
        }
        return updatedInstallment;
      }
      return installment;
    }));
  };
  const updateSupplier = (id: number, field: string, value: string) => {
    setSuppliers(suppliers.map(supplier => supplier.id === id ? {
      ...supplier,
      [field]: value
    } : supplier));
  };
  const updateRecipient = (installmentId: number, field: 'email' | 'phone', value: string) => {
    setInstallments(installments.map(installment => installment.id === installmentId && installment.recipient ? {
      ...installment,
      recipient: {
        ...installment.recipient,
        [field]: value
      }
    } : installment));
  };
  const splitOptions = [{
    value: 'dates',
    label: 'By payment schedule',
    icon: <CalendarDaysIcon size={16} data-id="element-2097" />
  }, {
    value: 'recipients',
    label: 'By multiple people',
    icon: <UsersIcon size={16} data-id="element-2098" />
  }, {
    value: 'commission',
    label: 'By commission',
    icon: <PercentIcon size={16} data-id="element-2099" />
  }];
  const beneficiaryOptions = [{
    value: 'percentage',
    label: 'Percentage',
    icon: <PercentIcon size={16} data-id="element-2100" />
  }, {
    value: 'fixed',
    label: 'Fixed amount',
    icon: <DollarSignIcon size={16} data-id="element-2101" />
  }];
  const totalAmount = installments.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  // Mock suppliers list for dropdown
  const suppliersList = [{
    id: 1,
    name: 'ABC Suppliers',
    email: 'abc@example.com'
  }, {
    id: 2,
    name: 'XYZ Merchants',
    email: 'xyz@example.com'
  }, {
    id: 3,
    name: '123 Distributors',
    email: '123@example.com'
  }, {
    id: 4,
    name: 'Global Partners',
    email: 'global@example.com'
  }];
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  const handleTotalPaymentAmountChange = (value: string) => {
    setTotalPaymentAmount(value);
  };
  const getCommissionAmountDisplay = () => {
    if (beneficiaryType === 'fixed') {
      return `RM ${supplierCommissionTotal.toFixed(2)} of RM ${parseFloat(totalPaymentAmount || '0').toFixed(2)}`;
    } else {
      const percentageTotal = suppliers.reduce((total, supplier) => {
        return total + (parseFloat(supplier.percentage || '0') || 0);
      }, 0);
      return `${percentageTotal.toFixed(2)}% of payment`;
    }
  };
  // Date picker helpers
  const toggleDatePicker = (id: number, type: 'due' | 'send') => {
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: prev[id] ? !prev[id][type] : true
      }
    }));
  };
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const handleDateSelect = (id: number, field: 'dueDate' | 'sendLinkDate', date: Date) => {
    updateInstallment(id, field, formatDate(date));
    // If selecting due date, update send link date
    if (field === 'dueDate') {
      const sendDate = new Date(date);
      sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
      updateInstallment(id, 'sendLinkDate', formatDate(sendDate));
    }
    // Close the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field === 'dueDate' ? 'due' : 'send']: false
      }
    }));
  };
  const generateCalendar = (dateStr: string) => {
    const today = new Date();
    const selectedDate = dateStr ? parseDate(dateStr) : today;
    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        // Fill remaining days of the week
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: selectedDate,
      weeks
    };
  };
  const parseDate = (dateString: string): Date => {
    if (!dateString) return new Date();
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date, selectedDateStr: string): boolean => {
    if (!selectedDateStr) return false;
    const selectedDate = parseDate(selectedDateStr);
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handlePrevMonth = (calendar, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() - 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  const handleNextMonth = (calendar, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() + 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  return <div className="space-y-6" data-id="element-2102">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2103">
        <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2104">
          Installment payment
        </h2>
        <FormField label="How would you like to split this payment?" tooltip="Choose how to split your payment" data-id="element-2105">
          <SegmentedControl options={splitOptions} value={splitType} onChange={value => setSplitType(value as 'dates' | 'recipients' | 'commission')} className="w-full md:w-auto" data-id="element-2106" />
        </FormField>
        <FormField label="Order reference" tooltip="A reference number or name for this payment" data-id="element-2107">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Order #1234 or Project Name" data-id="element-2108" />
        </FormField>
        {splitType === 'commission' && <>
            <FormField label="Total payment amount" required tooltip="The total amount that will be paid" data-id="element-2109">
              <div className="relative" data-id="element-2110">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2111">
                  <span className="text-gray-500" data-id="element-2112">RM</span>
                </div>
                <input type="text" className="block w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" value={totalPaymentAmount} onChange={e => handleTotalPaymentAmountChange(e.target.value)} data-id="element-2113" />
              </div>
            </FormField>
            <FormField label="Commission type" tooltip="Choose whether to use percentage or fixed amount for commissions" data-id="element-2114">
              <SegmentedControl options={beneficiaryOptions} value={beneficiaryType} onChange={value => setBeneficiaryType(value as 'percentage' | 'fixed')} className="w-full md:w-auto" data-id="element-2115" />
            </FormField>
          </>}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2116">
        <div className="mb-3" data-id="element-2117">
          <div className="flex justify-between items-center" data-id="element-2118">
            <h3 className="text-md font-medium text-gray-700" data-id="element-2119">
              {splitType === 'dates' ? 'Payment schedule' : splitType === 'recipients' ? 'Payment recipients' : 'Commission details'}
            </h3>
            <div className="text-sm text-gray-500" data-id="element-2120">
              {splitType === 'commission' ? `Commission: ${getCommissionAmountDisplay()}` : `Total: RM ${totalAmount.toFixed(2)}`}
            </div>
          </div>
        </div>
        {/* Commission section */}
        {splitType === 'commission' && <div className="space-y-3" data-id="element-2121">
            {commissionError && <div className="p-3 bg-red-50 text-red-700 rounded-md flex items-start" data-id="element-2122">
                <AlertCircleIcon size={16} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-2123" />
                <span className="text-sm" data-id="element-2124">{commissionError}</span>
              </div>}
            <div className="p-3 bg-blue-50 text-blue-700 rounded-md flex items-start mb-4" data-id="element-2125">
              <BuildingIcon size={16} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-2126" />
              <div className="text-sm" data-id="element-2127">
                <p data-id="element-2128">
                  Commission splitting allows you to distribute a payment
                  between your business and your partners or suppliers. You can:
                </p>
                <ul className="list-disc ml-5 mt-2" data-id="element-2129">
                  <li data-id="element-2130">Set commission as a percentage or fixed amount</li>
                  <li data-id="element-2131">Determine who pays the transaction fees</li>
                  <li data-id="element-2132">
                    Automatically split funds when the payment is processed
                  </li>
                </ul>
              </div>
            </div>
            {suppliers.map((supplier, index) => <div key={supplier.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-md" data-id="element-2133">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full font-medium text-sm" data-id="element-2134">
                  {index + 1}
                </div>
                <div className="flex-1" data-id="element-2135">
                  <div className="grid grid-cols-1 gap-3" data-id="element-2136">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2" data-id="element-2137">
                      <div data-id="element-2138">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2139">
                          Select partner/merchant
                        </label>
                        <select className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={supplier.name} onChange={e => {
                    const selected = suppliersList.find(s => s.name === e.target.value);
                    if (selected) {
                      updateSupplier(supplier.id, 'name', selected.name);
                      updateSupplier(supplier.id, 'email', selected.email);
                    }
                  }} data-id="element-2140">
                          <option value="" data-id="element-2141">Select a partner</option>
                          {suppliersList.map(s => <option key={s.id} value={s.name} data-id="element-2142">
                              {s.name}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2143">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2144">
                          Email
                        </label>
                        <input type="email" className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-gray-100" placeholder="Partner email" value={supplier.email} readOnly data-id="element-2145" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2146">
                      <div data-id="element-2147">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2148">
                          {beneficiaryType === 'percentage' ? 'Commission percentage' : 'Fixed amount'}
                        </label>
                        <div className="relative" data-id="element-2149">
                          {beneficiaryType === 'percentage' ? <>
                              <input type="number" className="block w-full pr-8 py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0" min="0" max="100" value={supplier.percentage} onChange={e => updateSupplier(supplier.id, 'percentage', e.target.value)} data-id="element-2150" />
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2151">
                                <span className="text-gray-500" data-id="element-2152">%</span>
                              </div>
                            </> : <>
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2153">
                                <span className="text-gray-500" data-id="element-2154">RM</span>
                              </div>
                              <input type="text" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" value={supplier.amount} onChange={e => updateSupplier(supplier.id, 'amount', e.target.value)} data-id="element-2155" />
                            </>}
                        </div>
                        {beneficiaryType === 'percentage' && parseFloat(supplier.percentage || '0') > 0 && totalPaymentAmount && <div className="text-xs text-gray-500 mt-1" data-id="element-2156">
                              ≈ RM
                              {(parseFloat(totalPaymentAmount) * parseFloat(supplier.percentage || '0') / 100).toFixed(2)}
                            </div>}
                      </div>
                      <div data-id="element-2157">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2158">
                          Description
                        </label>
                        <input type="text" className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Commission for services" value={supplier.description || ''} onChange={e => updateSupplier(supplier.id, 'description', e.target.value)} data-id="element-2159" />
                      </div>
                    </div>
                  </div>
                </div>
                <button type="button" onClick={() => removeSupplier(supplier.id)} className="text-gray-400 hover:text-red-500 mt-2" disabled={suppliers.length <= 1} data-id="element-2160">
                  <TrashIcon size={18} data-id="element-2161" />
                </button>
              </div>)}
            <button type="button" onClick={addSupplier} className="mt-3 flex items-center text-sm text-blue-600 hover:text-blue-800" data-id="element-2162">
              <PlusIcon size={16} className="mr-1" data-id="element-2163" />
              Add another partner
            </button>
          </div>}
        {/* Dates and Recipients sections (existing code) */}
        {(splitType === 'dates' || splitType === 'recipients') && <div className="space-y-3" data-id="element-2164">
            {splitType === 'dates' && <div className="p-3 bg-blue-50 text-blue-700 rounded-md flex items-start mb-4" data-id="element-2165">
                <InfoIcon size={16} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-2166" />
                <div className="text-sm" data-id="element-2167">
                  <p data-id="element-2168">
                    Payment links will be automatically sent on the "Send link
                    on" date. By default, links are sent {defaultExpiryDays}{' '}
                    days before the due date.
                  </p>
                </div>
              </div>}
            {installments.map((installment, index) => <div key={installment.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-md" data-id="element-2169">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full font-medium text-sm" data-id="element-2170">
                  {index + 1}
                </div>
                <div className="flex-1" data-id="element-2171">
                  <div className="grid grid-cols-1 gap-3" data-id="element-2172">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-2173">
                      <div data-id="element-2174">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2175">
                          Amount
                        </label>
                        <div className="relative" data-id="element-2176">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2177">
                            <span className="text-gray-500" data-id="element-2178">RM</span>
                          </div>
                          <input type="text" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" value={installment.amount} onChange={e => updateInstallment(installment.id, 'amount', e.target.value)} data-id="element-2179" />
                        </div>
                      </div>
                      {splitType === 'dates' ? <>
                          <div className="relative" data-id="element-2180">
                            <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2181">
                              Due date
                            </label>
                            <div className="relative" data-id="element-2182">
                              <input type="text" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" value={installment.dueDate} onChange={e => updateInstallment(installment.id, 'dueDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'due')} readOnly data-id="element-2183" />
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2184">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-2185" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'due')} data-id="element-2186">
                                {showDatePickers[installment.id]?.due ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-2187" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-2188" />}
                              </div>
                            </div>
                            {/* Date Picker for Due Date */}
                            {showDatePickers[installment.id]?.due && <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 w-full" data-id="element-2189">
                                {(() => {
                        const calendar = generateCalendar(installment.dueDate);
                        return <>
                                      <div className="flex justify-between items-center mb-2" data-id="element-2190">
                                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => handlePrevMonth(calendar, installment.id, 'due')} data-id="element-2191">
                                          <ChevronDownIcon size={16} className="transform rotate-90" data-id="element-2192" />
                                        </button>
                                        <div className="font-medium" data-id="element-2193">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => handleNextMonth(calendar, installment.id, 'due')} data-id="element-2194">
                                          <ChevronDownIcon size={16} className="transform -rotate-90" data-id="element-2195" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1" data-id="element-2196">
                                        <div className="text-gray-500" data-id="element-2197">Su</div>
                                        <div className="text-gray-500" data-id="element-2198">Mo</div>
                                        <div className="text-gray-500" data-id="element-2199">Tu</div>
                                        <div className="text-gray-500" data-id="element-2200">We</div>
                                        <div className="text-gray-500" data-id="element-2201">Th</div>
                                        <div className="text-gray-500" data-id="element-2202">Fr</div>
                                        <div className="text-gray-500" data-id="element-2203">Sa</div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-2204">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm
                                                  ${isSelected(date, installment.dueDate) ? 'bg-blue-600 text-white' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.dueDate) ? 'border border-blue-600 text-blue-600' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                                                `} onClick={() => handleDateSelect(installment.id, 'dueDate', date)} data-id="element-2205">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-2206"></div>))}
                                      </div>
                                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between" data-id="element-2207">
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 7);
                              handleDateSelect(installment.id, 'dueDate', date);
                            }} data-id="element-2208">
                                          7 days
                                        </button>
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 30);
                              handleDateSelect(installment.id, 'dueDate', date);
                            }} data-id="element-2209">
                                          30 days
                                        </button>
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 90);
                              handleDateSelect(installment.id, 'dueDate', date);
                            }} data-id="element-2210">
                                          90 days
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>
                          <div className="relative" data-id="element-2211">
                            <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2212">
                              Send link on
                            </label>
                            <div className="relative" data-id="element-2213">
                              <input type="text" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" value={installment.sendLinkDate} onChange={e => updateInstallment(installment.id, 'sendLinkDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'send')} readOnly data-id="element-2214" />
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2215">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-2216" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'send')} data-id="element-2217">
                                {showDatePickers[installment.id]?.send ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-2218" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-2219" />}
                              </div>
                            </div>
                            {/* Date Picker for Send Link Date */}
                            {showDatePickers[installment.id]?.send && <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 w-full" data-id="element-2220">
                                {(() => {
                        const calendar = generateCalendar(installment.sendLinkDate);
                        return <>
                                      <div className="flex justify-between items-center mb-2" data-id="element-2221">
                                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => handlePrevMonth(calendar, installment.id, 'send')} data-id="element-2222">
                                          <ChevronDownIcon size={16} className="transform rotate-90" data-id="element-2223" />
                                        </button>
                                        <div className="font-medium" data-id="element-2224">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => handleNextMonth(calendar, installment.id, 'send')} data-id="element-2225">
                                          <ChevronDownIcon size={16} className="transform -rotate-90" data-id="element-2226" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1" data-id="element-2227">
                                        <div className="text-gray-500" data-id="element-2228">Su</div>
                                        <div className="text-gray-500" data-id="element-2229">Mo</div>
                                        <div className="text-gray-500" data-id="element-2230">Tu</div>
                                        <div className="text-gray-500" data-id="element-2231">We</div>
                                        <div className="text-gray-500" data-id="element-2232">Th</div>
                                        <div className="text-gray-500" data-id="element-2233">Fr</div>
                                        <div className="text-gray-500" data-id="element-2234">Sa</div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-2235">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm
                                                  ${isSelected(date, installment.sendLinkDate) ? 'bg-blue-600 text-white' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.sendLinkDate) ? 'border border-blue-600 text-blue-600' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                                                `} onClick={() => handleDateSelect(installment.id, 'sendLinkDate', date)} data-id="element-2236">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-2237"></div>))}
                                      </div>
                                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between" data-id="element-2238">
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 7);
                              handleDateSelect(installment.id, 'sendLinkDate', date);
                            }} data-id="element-2239">
                                          7 days
                                        </button>
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 14);
                              handleDateSelect(installment.id, 'sendLinkDate', date);
                            }} data-id="element-2240">
                                          14 days
                                        </button>
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              handleDateSelect(installment.id, 'sendLinkDate', date);
                            }} data-id="element-2241">
                                          Today
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>
                        </> : <div className="col-span-2" data-id="element-2242">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2243">
                            Recipient contact information
                          </label>
                          <div className="space-y-2" data-id="element-2244">
                            <div className="flex items-center relative" data-id="element-2245">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2246">
                                <MailIcon size={16} className="text-gray-400" data-id="element-2247" />
                              </div>
                              <input type="email" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Email address" value={installment.recipient?.email || ''} onChange={e => updateRecipient(installment.id, 'email', e.target.value)} data-id="element-2248" />
                            </div>
                            <div className="flex items-center relative" data-id="element-2249">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2250">
                                <SmartphoneIcon size={16} className="text-gray-400" data-id="element-2251" />
                              </div>
                              <input type="tel" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Phone number" value={installment.recipient?.phone || ''} onChange={e => updateRecipient(installment.id, 'phone', e.target.value)} data-id="element-2252" />
                            </div>
                          </div>
                        </div>}
                    </div>
                    <div data-id="element-2253">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2254">
                        Description
                      </label>
                      <input type="text" className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={splitType === 'dates' ? 'e.g., First installment' : "e.g., John's share"} value={installment.description} onChange={e => updateInstallment(installment.id, 'description', e.target.value)} data-id="element-2255" />
                    </div>
                  </div>
                </div>
                <button type="button" onClick={() => removeInstallment(installment.id)} className="text-gray-400 hover:text-red-500 mt-2" disabled={installments.length <= 1} data-id="element-2256">
                  <TrashIcon size={18} data-id="element-2257" />
                </button>
              </div>)}
            <button type="button" onClick={addInstallment} className="mt-3 flex items-center text-sm text-blue-600 hover:text-blue-800" data-id="element-2258">
              <PlusIcon size={16} className="mr-1" data-id="element-2259" />
              Add another {splitType === 'dates' ? 'payment date' : 'recipient'}
            </button>
          </div>}
      </div>
      {/* Fee configuration section - moved to its own card */}
      {splitType === 'commission' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2260">
          <h4 className="font-medium text-gray-700 mb-3" data-id="element-2261">Fee Configuration</h4>
          <div className="space-y-3" data-id="element-2262">
            <label className="flex items-center space-x-2" data-id="element-2263">
              <input type="radio" name="feeType" checked={feeType === 'supplier'} onChange={() => setFeeType('supplier')} className="h-4 w-4 text-blue-600" data-id="element-2264" />
              <span data-id="element-2265">Partner pays the fee</span>
            </label>
            <label className="flex items-center space-x-2" data-id="element-2266">
              <input type="radio" name="feeType" checked={feeType === 'merchant'} onChange={() => setFeeType('merchant')} className="h-4 w-4 text-blue-600" data-id="element-2267" />
              <span data-id="element-2268">My business pays the fee</span>
            </label>
            <label className="flex items-center space-x-2" data-id="element-2269">
              <input type="radio" name="feeType" checked={feeType === 'split'} onChange={() => setFeeType('split')} className="h-4 w-4 text-blue-600" data-id="element-2270" />
              <span data-id="element-2271">Split the fee</span>
            </label>
            {feeType === 'split' && <div className="pl-6 mt-2" data-id="element-2272">
                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2273">
                  Partner fee percentage
                </label>
                <div className="relative w-1/3" data-id="element-2274">
                  <input type="number" className="block w-full pr-8 py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="50" min="0" max="100" defaultValue="50" data-id="element-2275" />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2276">
                    <span className="text-gray-500" data-id="element-2277">%</span>
                  </div>
                </div>
              </div>}
          </div>
        </div>}
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} data-id="element-2278" />
    </div>;
}